import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/atoms/button";
import { type DocumentSummary } from "~/api/types";
import { DocumentArtifactStatus } from "~/config/types";
import { Utils } from "~/api/utils";
import { ProgressBar } from "~/atoms/progress-bar";
import { toast } from "react-toastify";
import CheckboxWithLabel from "~/molecules/CheckboxWithLabel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";

interface DownloadDocumentsModalProps {
  trigger: ReactNode;
  documents: DocumentSummary[];
}

export const DownloadDocumentsModal = ({
  trigger,
  documents,
}: DownloadDocumentsModalProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(($) => $.commons.download.documents)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.commons.download.description)}
          </DialogDescription>
        </DialogHeader>
        <DownloadDocuments
          documents={documents}
          onClose={() => setIsOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  documents: DocumentSummary[];
  onClose: () => void;
}

const DownloadDocuments = ({ documents, onClose }: Props) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);

  const downloadableReleases = Utils.getDownloadableReleases(documents);
  const availableStatuses = Utils.getReleaseStatuses(downloadableReleases);

  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    availableStatuses.slice(0, 1),
  );

  const toggleStatus = (status: DocumentArtifactStatus, checked: boolean) =>
    setSelectedStatuses((current) =>
      checked ? [...current, status] : current.filter((s) => s !== status),
    );

  const statusLabel: Record<DocumentArtifactStatus, string> = {
    [DocumentArtifactStatus.published]: t(
      ($) => $.commons.document.statuses.published,
    ),
    [DocumentArtifactStatus.draft]: t(($) => $.commons.document.statuses.draft),
    [DocumentArtifactStatus.archived]: t(
      ($) => $.commons.document.statuses.archived,
    ),
  };

  const downloads = downloadableReleases.filter((download) =>
    selectedStatuses.includes(download.status),
  );
  const selectedDocumentCount = new Set(
    downloads.map((download) => download.document.id),
  ).size;

  const downloadDocuments = async () => {
    if (downloads.length === 0) {
      toast.info(t(($) => $.toasts.info.noDocumentsFound));
      return;
    }

    try {
      setError(false);
      setIsDownloading(true);
      setProgress(0);
      await Utils.saveZipDocuments(downloads, setProgress);
      onClose();
    } catch {
      setError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isDownloading) {
    return (
      <div className="grid gap-6">
        <ProgressBar value={(progress / Math.max(downloads.length, 1)) * 100} />
        <div>
          {t(($) => $.commons.download.downloading)} {progress} /{" "}
          {downloads.length}
        </div>
        {error && (
          <div className="text-destructive">
            {t(($) => $.commons.download.error)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <DialogDescription>
        {t(($) => $.library.filters.selectedCount, {
          count: selectedDocumentCount,
        })}
      </DialogDescription>
      {availableStatuses.length > 1 && (
        <div className="grid grid-flow-col">
          <div className="text-sm font-medium">
            {t(($) => $.commons.download.versionsToInclude)}
          </div>
          {availableStatuses.map((status) => (
            <CheckboxWithLabel
              key={status}
              id={`download-${status}-versions`}
              label={statusLabel[status]}
              checked={selectedStatuses.includes(status)}
              onCheckedChange={(checked) => toggleStatus(status, checked)}
            />
          ))}
        </div>
      )}
      {error && (
        <div className="text-destructive">
          {t(($) => $.commons.download.error)}
        </div>
      )}

      <DialogFooter>
        <Button
          onClick={downloadDocuments}
          disabled={selectedStatuses.length === 0}
        >
          {t(($) => $.commons.buttons.download)}
        </Button>
      </DialogFooter>
    </div>
  );
};
